import { mapAllocationEdgesToStopIds } from './allocationEdgeDraft';
import type { DeliveryGroupDraft } from './deliveryGroupTypes';
import {
  determineExecutionRelations,
  executionRelationsToAllocationLinks
} from './executionAuthorityResolver';
import type {
  AllocationEdgeDraft,
  FromStopDraftByUid,
  QuantityDraftByCargo,
  ToStopDraftByUid
} from './executionDraftTypes';
import type { LoadingUnloadingAllocationLink } from './loadingUnloadingLinkHelpers';
import { resolveRouteStopsSavePayload } from './resolveRouteStopsSavePayload';
import {
  prepareStopsCompatPayload,
  type MainStopFormFields,
  type RouteStopSaveRow
} from './routeStopsSavePipeline';

export type ExecutionSnapshotStop = {
  stopUid: string;
  position: number;
  groupUid: string;
  type: 'loading' | 'unloading';
};

export type ExecutionSnapshotAllocation = {
  cargoId: number;
  fromStopUid: string;
  toStopUid: string;
  quantity: number;
  fromPosition: number;
  toPosition: number;
};

export type ImmutableExecutionSnapshot = {
  readonly frozenAt: number;
  readonly loadingStops: readonly ExecutionSnapshotStop[];
  readonly unloadingStops: readonly ExecutionSnapshotStop[];
  readonly allocations: readonly ExecutionSnapshotAllocation[];
  readonly allocationLinks: readonly LoadingUnloadingAllocationLink[];
  readonly saveRows: readonly RouteStopSaveRow[];
  readonly payloadStops: readonly Record<string, unknown>[];
  readonly positionByStopUid: ReadonlyMap<string, number>;
  readonly stopUidByPosition: ReadonlyMap<number, string>;
};

export function buildImmutableExecutionSnapshot(params: {
  groups: DeliveryGroupDraft[];
  mainLoading: MainStopFormFields;
  mainUnloading: MainStopFormFields;
  selectedIds: number[];
  fromStopDraft: FromStopDraftByUid;
  toStopDraft: ToStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
  allocationEdges?: AllocationEdgeDraft[];
}): ImmutableExecutionSnapshot {
  const { rows: saveRows } = resolveRouteStopsSavePayload({
    groups: params.groups,
    mainLoading: params.mainLoading,
    mainUnloading: params.mainUnloading
  });

  const positionByStopUid = new Map<string, number>();
  const stopUidByPosition = new Map<number, string>();

  for (const row of saveRows) {
    if (!row._stopUid) continue;
    positionByStopUid.set(row._stopUid, row.position);
    stopUidByPosition.set(row.position, row._stopUid);
  }

  const loadingStops: ExecutionSnapshotStop[] = [];
  const unloadingStops: ExecutionSnapshotStop[] = [];

  for (const row of saveRows) {
    if (!row._stopUid) continue;
    const entry: ExecutionSnapshotStop = {
      stopUid: row._stopUid,
      position: row.position,
      groupUid: row._groupUid ?? '',
      type: row.type
    };
    if (row.type === 'loading') loadingStops.push(entry);
    else unloadingStops.push(entry);
  }

  const relations = determineExecutionRelations({
    selectedIds: params.selectedIds,
    fromStopDraft: params.fromStopDraft,
    toStopDraft: params.toStopDraft,
    quantityDraft: params.quantityDraft,
    positionByStopUid,
    allocationEdges: params.allocationEdges
  });

  const allocations: ExecutionSnapshotAllocation[] = relations.map((r) => ({ ...r }));
  const allocationLinks = executionRelationsToAllocationLinks(relations);
  const payloadStops = prepareStopsCompatPayload([...saveRows]);

  return Object.freeze({
    frozenAt: Date.now(),
    loadingStops: Object.freeze(loadingStops),
    unloadingStops: Object.freeze(unloadingStops),
    allocations: Object.freeze(allocations),
    allocationLinks: Object.freeze(allocationLinks),
    saveRows: Object.freeze(saveRows),
    payloadStops: Object.freeze(payloadStops),
    positionByStopUid,
    stopUidByPosition
  });
}

export function toCompatibilityExecutionSnapshot(snapshot: ImmutableExecutionSnapshot) {
  return {
    frozenAt: snapshot.frozenAt,
    stops: [...snapshot.saveRows]
      .filter((row) => row._stopUid)
      .sort((a, b) => a.position - b.position)
      .map((row) => ({
        stopUid: row._stopUid!,
        position: row.position,
        groupUid: row._groupUid ?? '',
        type: row.type
      })),
    allocations: snapshot.allocations.map((a) => ({
      cargoId: a.cargoId,
      fromStopUid: a.fromStopUid,
      toStopUid: a.toStopUid,
      quantity: a.quantity
    }))
  };
}

export function mapSnapshotAllocationsToStopIds(
  snapshot: ImmutableExecutionSnapshot,
  savedStops: Array<{
    id: number;
    position: number;
    clientExecutionUid?: string;
    stopUid?: string;
  }>
): Array<{ cargoId: number; quantity: number; fromStopId?: number; toStopId?: number }> {
  const idByUid = new Map<string, number>();
  for (const s of savedStops) {
    const uid = String(s.clientExecutionUid ?? s.stopUid ?? '').trim();
    if (uid) idByUid.set(uid, s.id);
  }
  const idByPosition = new Map(savedStops.map((s) => [s.position, s.id]));

  return snapshot.allocations.map((a) => ({
    cargoId: a.cargoId,
    quantity: a.quantity,
    fromStopId: idByUid.get(a.fromStopUid) ?? idByPosition.get(a.fromPosition),
    toStopId: idByUid.get(a.toStopUid) ?? idByPosition.get(a.toPosition)
  }));
}

export function positionByStopUidFromSaveRows(
  rows: Array<{ _stopUid?: string; position: number }>
): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of rows) {
    if (row._stopUid) map.set(row._stopUid, row.position);
  }
  return map;
}

export function mapUidDraftsToStopIds(params: {
  cargoIds: number[];
  fromStopDraft: FromStopDraftByUid;
  toStopDraft: ToStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
  allocationEdges?: AllocationEdgeDraft[];
  positionByStopUid: Map<string, number>;
  savedStops: Array<{
    id: number;
    position: number;
    clientExecutionUid?: string;
    stopUid?: string;
  }>;
  defaultFromStopUid?: string;
  defaultToStopUid?: string;
  isSelected?: (cargoId: number) => boolean;
}): Array<{ cargoId: number; quantity: number; fromStopId?: number; toStopId?: number }> {
  if (params.allocationEdges && params.allocationEdges.length > 0) {
    return mapAllocationEdgesToStopIds({
      edges: params.allocationEdges,
      positionByStopUid: params.positionByStopUid,
      savedStops: params.savedStops
    });
  }
  const idByPosition = new Map(params.savedStops.map((s) => [s.position, s.id]));
  const idByUid = new Map<string, number>();
  for (const stop of params.savedStops) {
    const uid = String(stop.clientExecutionUid ?? stop.stopUid ?? '').trim();
    if (uid) idByUid.set(uid, stop.id);
  }

  const resolveStopId = (uid: string | undefined, position: number | undefined): number | undefined => {
    if (uid) {
      const byUid = idByUid.get(uid);
      if (byUid != null) return byUid;
    }
    if (position !== undefined) {
      const byPos = idByPosition.get(position);
      if (byPos != null) return byPos;
    }
    return undefined;
  };

  return params.cargoIds.map((cargoId) => {
    const selected = params.isSelected ? params.isSelected(cargoId) : true;
    const quantity = selected ? Number(params.quantityDraft[cargoId] ?? 0) : 0;
    const fromUid = params.fromStopDraft[cargoId] ?? params.defaultFromStopUid;
    const toUid = params.toStopDraft[cargoId] ?? params.defaultToStopUid;
    const fromPos = fromUid ? params.positionByStopUid.get(fromUid) : undefined;
    const toPos = toUid ? params.positionByStopUid.get(toUid) : undefined;
    return {
      cargoId,
      quantity,
      fromStopId: resolveStopId(fromUid, fromPos),
      toStopId: resolveStopId(toUid, toPos)
    };
  });
}

export function findAllocationStopMappingIssue(
  rows: Array<{ cargoId: number; quantity: number; fromStopId?: number; toStopId?: number }>
): string | null {
  for (const row of rows) {
    if (row.quantity <= 0) continue;
    if (row.fromStopId == null) {
      return `Груз #${row.cargoId}: не найдена точка погрузки после сохранения маршрута`;
    }
    if (row.toStopId == null) {
      return `Груз #${row.cargoId}: не найдена точка выгрузки после сохранения маршрута`;
    }
  }
  return null;
}
